import { gql, type TypedDocumentNode } from '@apollo/client';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { BrandXS } from '@sorare/core/src/atoms/typography/index.tsx';
import {
  CornerBadge,
  Position,
} from '@sorare/core/src/components/CornerBadge/index.tsx';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage/index.tsx';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText/index.tsx';

import { TooltipContent } from '#src/components/series/SeriesBoard/HotStreakTile/TooltipContent/index.tsx';

import type { TaskTooltipContent_thresholdPickerTask } from './__generated__/index.graphql.ts';

type Props = {
  task: TaskTooltipContent_thresholdPickerTask;
  showTargetValue: boolean;
  oldThreshold: number | null;
};

const PositionedBadge = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  transform: translateY(-50%);
  z-index: 1;
`;

export const TaskTooltipContent = ({
  task,
  showTargetValue,
  oldThreshold,
}: Props) => {
  const { rewardConfigs, target, replay } = task;
  const hasReducedThreshold =
    !replay && !!oldThreshold && oldThreshold > target;

  const rewardConfig = task.rewardConfigs.at(0);
  const badgeRewardConfig = rewardConfigs.at(1);

  if (!rewardConfig) {
    return null;
  }

  return (
    <>
      {!!badgeRewardConfig && (
        <PositionedBadge>
          <CornerBadge color="var(--c-gradient-limited)" align={Position.RIGHT}>
            <Horizontal gap={0} center style={{ color: 'var(--c-white)' }}>
              <BrandXS>+</BrandXS>
              <RewardConfigImage anyRewardConfig={badgeRewardConfig} size={2} />
              <BrandXS>
                <RewardConfigText anyRewardConfig={badgeRewardConfig} />
              </BrandXS>
            </Horizontal>
          </CornerBadge>
        </PositionedBadge>
      )}
      <TooltipContent
        target={task.target}
        showTargetValue={showTargetValue}
        hasReducedThreshold={hasReducedThreshold}
        oldThreshold={oldThreshold}
        rewardConfig={rewardConfig}
      />
    </>
  );
};

TaskTooltipContent.fragments = {
  thresholdPickerTask: gql`
    fragment TaskTooltipContent_thresholdPickerTask on ThresholdPickerTask {
      id
      target
      replay
      rewardConfigs {
        id
        ...TooltipContent_anyRewardConfigInterface
        ...RewardConfigImage_anyRewardConfigInterface
        ...RewardConfigText_anyRewardConfigInterface
      }
    }
    ${TooltipContent.fragments.anyRewardConfigInterface}
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<TaskTooltipContent_thresholdPickerTask>,
};
